import * as React from "react";
import { useEventListener } from "usehooks-ts";
import {
  data_focus,
  DATA_FOCUS_ADD,
  UseQuestionInputClauses,
} from "./definitions";
import styles from "./question-input.module.css";

export function InputContainer({
  clauses: { list, removeClause },
  children,
  addAfter,
  addBefore,
  alternateStyle,
  collapsed,
  floatAway,
  onBlur,
  onFocus,
}: React.PropsWithChildren<{
  addAfter?: React.ReactNode;
  addBefore?: React.ReactNode;
  alternateStyle?: boolean;
  clauses: UseQuestionInputClauses;
  collapsed?: boolean;
  floatAway?: boolean;
  onFocus?: () => void;
  onBlur?: () => void;
}>) {
  const { ref, onClick } = useKeyboardInInput({ clauses: list, removeClause });
  const classNames = [styles.input];
  if (list.length === 0) classNames.push(styles.empty);
  if (addAfter) classNames.push(styles.hasAddAfter);
  if (collapsed) classNames.push(styles.collapsed);

  const [focused, setFocused] = React.useState(false);
  let hasFocus = ref.current?.contains(document.activeElement);
  hasFocus =
    hasFocus || Boolean(ref.current?.querySelector(`.${styles.hasOpenMenu}`));
  const activeElement = useFocusChange().focused;

  React.useEffect(() => {
    if (!focused && hasFocus) {
      setFocused(true);
      onFocus && onFocus();
    } else if (focused && !hasFocus) {
      setFocused(false);
      onBlur && onBlur();
    }
  }, [focused, hasFocus, onFocus, onBlur, activeElement]);

  return (
    <div
      style={
        floatAway
          ? {
              height: 34,
              overflow: "visible",
              borderRadius: "inherit",
              backgroundColor: "inherit",
            }
          : { borderRadius: "inherit" }
      }
    >
      <div
        className={`${styles.container} ${
          alternateStyle ? styles.alternate : ""
        } ${floatAway ? styles.floatAway : ""} ${
          collapsed ? styles.collapsed : ""
        }`}
        style={floatAway ? { zIndex: 10, position: "relative" } : {}}
      >
        {addBefore && <div className={styles.addBefore}>{addBefore}</div>}
        <div ref={ref} onClick={onClick} className={classNames.join(" ")}>
          {children}
        </div>
        {addAfter && <div className={styles.addAfter}>{addAfter}</div>}
      </div>
    </div>
  );
}

function useFocusChange() {
  const [focused, setFocused] =
    React.useState<typeof document.activeElement>(null);
  const focusIn = React.useCallback(
    () => setFocused(document.activeElement),
    []
  );
  const focusOut = React.useCallback(() => setFocused(null), []);
  useEventListener("focusout", focusOut);
  useEventListener("focusin", focusIn);
  return { focused };
}

function useKeyboardInInput({
  clauses,
  removeClause,
}: {
  clauses: UseQuestionInputClauses["list"];
  removeClause: UseQuestionInputClauses["removeClause"];
}) {
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const propsRef = React.useRef({ clauses, removeClause });
  propsRef.current = { clauses, removeClause };
  React.useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (
        containerRef.current &&
        containerRef.current.contains(document.activeElement)
      ) {
        const focusables = [
          ...containerRef.current.querySelectorAll(data_focus()),
        ];
        const index = focusables.indexOf(document.activeElement!);
        if (event.key === "Escape") {
          event.preventDefault();
          (document.activeElement as HTMLInputElement | undefined)?.blur();
        } else if (["Backspace", "Delete"].includes(event.key)) {
          event.preventDefault();
          if (index < propsRef.current.clauses.length) {
            propsRef.current.removeClause(propsRef.current.clauses.at(index)!);
            containerRef.current?.click();
          }
        } else if (event.key === "ArrowLeft" && index > 0) {
          event.preventDefault();
          const prev = focusables[index - 1] as HTMLInputElement;
          prev?.focus();
        } else if (
          event.key === "ArrowRight" &&
          index < focusables.length - 1
        ) {
          event.preventDefault();
          const next = focusables[index + 1] as HTMLInputElement;
          next?.focus();
        }
      }
    };
    document.addEventListener("keydown", handleKeyPress);
    return () => document.removeEventListener("keydown", handleKeyPress);
  }, []);

  const clauseCount = clauses.length;
  const onClick: React.MouseEventHandler = React.useCallback(
    (e) => {
      const addButton = (e.target as HTMLElement).querySelector(
        data_focus(DATA_FOCUS_ADD)
      ) as HTMLInputElement | undefined;
      if (clauseCount > 0) addButton?.focus();
      else addButton?.click();
    },
    [clauseCount]
  );
  return { ref: containerRef, onClick };
}
